import { useMemo, useCallback } from 'react';
import { usePermissions } from '@/contexts/PermissionContext';
import type { MenuItem } from '../types';

/**
 * Hook that determines which menu items should be visible based on user permissions.
 * Returns filtered menu items and settings items with permission-hidden pages removed.
 *
 * Mapping logic:
 * - Homepage & Timesheet: always visible (every user has at least 'own' access)
 * - Approvals: visible if canViewApprovals() or canViewApprovalDashboard()
 * - Reports (parent): visible if any child report is enabled
 *   - Timesheet Reports: canViewReport('timesheet')
 *   - Missing Timesheets: reportAccessPermissions.missingTimesheetReport.enabled
 *   - Utilisation: canViewReport('utilisation')
 *   - Billable Hours: removed — belongs in the Projects module of the PSA suite
 * - Analytics: removed — belongs in PSA suite cross-module Analytics/BI module
 * - Team (parent): canViewEmployeeDirectory()
 * - Projects: always visible (users see assigned projects)
 * - Import/Export: always visible (placeholder pages, no permission type yet)
 * - Settings (parent): canAccessAnySettings() or canViewIntegrations() or canViewCompanies()
 *   - General: canManageSystemSettings()
 *   - Roles & Permissions: canManageRolesPermissions()
 *   - Approvals (settings): canManageApprovalWorkflows()
 *   - Screen Data Modes: canManageSystemSettings()
 *   - Roles & Rates: canManageRates()
 *   - Notifications: canManageSystemSettings()
 *   - Security: canManageSystemSettings()
 *   - Integrations: canViewIntegrations()
 */
export function useMenuPermissions(
  allMenuItems: MenuItem[],
  allSettingsItems: MenuItem[]
) {
  const {
    permissions,
    isLoading,
    canViewPersonalHomepage,
    canViewApprovals,
    canViewApprovalDashboard,
    canViewReport,
    canAccessAnyReports,
    canViewEmployeeDirectory,
    canManageRolesPermissions,
    canManageSystemSettings,
    canManageRates,
    canManageApprovalWorkflows,
    canAccessAnySettings,
    canViewIntegrations,
    canDelegateApproval,
  } = usePermissions();

  // Helper: filter children of menu items based on per-child permissions.
  // This runs for ALL users (including admins) so that feature-level
  // toggles like canDelegateApproval are always respected.
  const filterMenuChildren = useCallback((items: MenuItem[]): MenuItem[] => {
    return items.map((item) => {
      // For parent items with children, also filter children based on permissions
      if (item.id === 'reports' && item.children) {
        const filteredChildren = item.children.filter((child) => {
          switch (child.id) {
            case 'reports-timesheet':
              return canViewReport('timesheet');
            case 'reports-missing-timesheets':
              return permissions?.reportAccessPermissions?.missingTimesheetReport?.enabled ?? false;
            case 'reports-utilisation':
              return canViewReport('utilisation');
            case 'reports-billable':
              // Billable Hours removed — belongs in the Projects module of the PSA suite.
              return false;
            default:
              return true;
          }
        });
        // If no children visible after filtering, hide the parent too
        if (filteredChildren.length === 0) return null;
        return { ...item, children: filteredChildren };
      }

      // For the approvals item, filter the My Delegations child by canDelegateApproval
      if (item.id === 'approvals' && item.children) {
        const filteredChildren = item.children.filter((child) => {
          if (child.id === 'approvals-my-delegations') {
            return canDelegateApproval();
          }
          return true;
        });
        // If only the dashboard child remains, collapse back to a flat item (no children)
        if (filteredChildren.length <= 1) {
          return { ...item, children: undefined };
        }
        return { ...item, children: filteredChildren };
      }

      return item;
    }).filter((item): item is MenuItem => item !== null);
  }, [permissions, canViewReport, canDelegateApproval]);

  // Helper: check whether the role has ANY approval-related permission enabled.
  // Used to decide whether the Approvals menu is shown — even for admins.
  const hasAnyApprovalPermission = useCallback((): boolean => {
    return canViewApprovals() || canViewApprovalDashboard();
  }, [canViewApprovals, canViewApprovalDashboard]);

  const filteredMenuItems = useMemo(() => {
    // While permissions are loading, show all items to avoid flash of missing content
    if (isLoading || !permissions) return allMenuItems;

    // Admin users (anyone with system settings/admin access) see all pages
    // but still apply per-child filtering (e.g. canDelegateApproval)
    // AND hide the Approvals menu when the role has no approval permissions.
    if (canAccessAnySettings()) {
      return filterMenuChildren(allMenuItems.filter((item) => {
        if (item.id === 'approvals') return hasAnyApprovalPermission();
        return true;
      }));
    }

    return filterMenuChildren(allMenuItems.filter((item) => {
      switch (item.id) {
        // Personal homepage — gated by 'dashboard.view_own' permission code
        case 'homepage':
          return canViewPersonalHomepage();

        // Always visible
        case 'timesheet':
        case 'projects':
        case 'data':
          return true;

        // Approvals: visible if user has any approval permission
        case 'approvals':
          return hasAnyApprovalPermission();

        // Reports: visible if any report type is enabled
        case 'reports':
          return canAccessAnyReports();

        // Analytics: removed from Timesheet module — belongs in PSA suite cross-module Analytics/BI
        case 'analytics':
          return false;

        // Team: requires employee directory access
        case 'team':
          return canViewEmployeeDirectory();

        // Default: show the item
        default:
          return true;
      }
    }));
  }, [
    allMenuItems,
    permissions,
    isLoading,
    canAccessAnySettings,
    hasAnyApprovalPermission,
    canAccessAnyReports,
    canViewEmployeeDirectory,
    filterMenuChildren,
  ]);

  const filteredSettingsItems = useMemo(() => {
    // While permissions are loading, show all items to avoid flash of missing content
    if (isLoading || !permissions) return allSettingsItems;

    // Admin users see all settings pages
    if (canAccessAnySettings()) return allSettingsItems;

    return allSettingsItems.filter((item) => {
      switch (item.id) {
        case 'settings-roles':
          return canManageRates();

        case 'settings-general':
        case 'settings-screens':
        case 'settings-notifications':
        case 'settings-security':
          return canManageSystemSettings();

        case 'settings-roles-permissions':
          return canManageRolesPermissions();

        case 'settings-approvals':
          return canManageApprovalWorkflows();

        case 'settings-approvals-delegation':
          return canManageApprovalWorkflows();

        case 'settings-approvals-delegation-audit':
          return canManageApprovalWorkflows();

        case 'integrations':
          return canViewIntegrations();

        default:
          return true;
      }
    });
  }, [
    allSettingsItems,
    permissions,
    isLoading,
    canAccessAnySettings,
    canManageSystemSettings,
    canManageRates,
    canManageRolesPermissions,
    canManageApprovalWorkflows,
    canViewIntegrations,
  ]);

  // Settings section should be hidden entirely if no settings items are visible
  const showSettings = useMemo(() => {
    if (isLoading || !permissions) return true;
    return filteredSettingsItems.length > 0;
  }, [isLoading, permissions, filteredSettingsItems]);

  return {
    filteredMenuItems,
    filteredSettingsItems,
    showSettings,
  };
}
